// Справочники карты «Рядом с домом» — ТОЛЬКО из design/content/map-taxonomy.json
// (решение владельца 06.10.2026). В базе — code, подписи — отсюда по языку.
// Здесь только типы и помощники, своих списков нет.
import taxonomy from "../../design/content/map-taxonomy.json";

export type Lang3 = "az" | "ru" | "en";
export type TaxItem = { code: string; az: string; ru: string; en: string };
export type TaxCategory = TaxItem & { items: TaxItem[] };
/** districts — районы станции (у пограничных несколько): мягкая проверка пина в анкете. */
export type Metro = TaxItem & { lat: number; lng: number; districts: string[] };
/** Услуга верхнего уровня (не предмет в категории) — со своими классами. */
export type TaxService = TaxItem & { grades: string[] };

export const TUTOR_CATEGORIES = taxonomy.tutor_categories as TaxCategory[];
/**
 * Услуги репетитора верхнего уровня — над категориями предметов. Сейчас одна:
 * homework («Помощь с домашним заданием», классы 1–4 и 5–9). Код услуги
 * хранится в subjects наравне с предметами, а категорией у него — он сам.
 */
export const TUTOR_SERVICES = taxonomy.tutor_services as TaxService[];
/** Быстрые запросы на карте для слоя «Репетиторы» — по порядку, первый — homework. */
export const TUTOR_QUICK = taxonomy.tutor_quick as string[];
export const CLUB_CATEGORIES = taxonomy.club_categories as TaxCategory[];
export const DISTRICTS = taxonomy.districts as TaxItem[];
export const METRO = taxonomy.metro as Metro[];
/**
 * Код Баку в map_partners.city. Справочник городов — таблица map_cities
 * (20261028120000, src/lib/map-cities.ts), не этот файл. Только в Баку есть
 * районы (DISTRICTS) и метро; вне Баку — город целиком.
 */
export const BAKU_CITY = "baku";
export const ADDRESS_MODES = taxonomy.address_modes as TaxItem[];
export const FORMATS = taxonomy.formats as TaxItem[];
/** Классы репетитора (мультивыбор). */
export const GRADES = taxonomy.grades as TaxItem[];

/** Три режима (решение владельца 06.10.2026): точный пин, выезд по районам, онлайн. */
export type AddressMode = "building" | "travel" | "online";
export type PartnerKind = "tutor" | "club";

export const categoriesFor = (kind: PartnerKind) => (kind === "tutor" ? TUTOR_CATEGORIES : CLUB_CATEGORIES);
/** Услуги верхнего уровня: у репетитора — TUTOR_SERVICES, у кружка — нет. */
export const servicesFor = (kind: PartnerKind): TaxService[] => (kind === "tutor" ? TUTOR_SERVICES : []);
/** Всё, что можно выбрать в subjects: сначала услуги, потом предметы категорий. */
export const subjectItemsFor = (kind: PartnerKind): TaxItem[] => [...servicesFor(kind), ...categoriesFor(kind).flatMap((c) => c.items)];

/** Категория, в которой лежит предмет данного вида партнёра. У услуги — она сама. */
export function categoryOf(kind: PartnerKind, subject: string): string | null {
  if (servicesFor(kind).some((s) => s.code === subject)) return subject;
  return categoriesFor(kind).find((c) => c.items.some((i) => i.code === subject))?.code ?? null;
}

/** Подпись категории (или услуги верхнего уровня) по коду. */
export const categoryLabel = (kind: PartnerKind, code: string, lang: Lang3): string =>
  labelOf([...servicesFor(kind), ...categoriesFor(kind)], code, lang);

export const isSubjectOf = (kind: PartnerKind, code: string) => categoryOf(kind, code) !== null;
export const isDistrict = (code: string) => DISTRICTS.some((d) => d.code === code);
export const isFormat = (code: string) => FORMATS.some((f) => f.code === code);
export const isGrade = (code: string) => GRADES.some((g) => g.code === code);

export function labelOf(list: TaxItem[], code: string, lang: Lang3): string {
  return list.find((x) => x.code === code)?.[lang] ?? code;
}

/** Ближайшая станция метро — простая формула расстояния, без внешних API. */
export function nearestMetro(lat: number, lng: number): Metro {
  const k = Math.cos((lat * Math.PI) / 180);
  let best = METRO[0];
  let bestD = Infinity;
  for (const m of METRO) {
    const d = (m.lat - lat) ** 2 + ((m.lng - lng) * k) ** 2;
    if (d < bestD) {
      bestD = d;
      best = m;
    }
  }
  return best;
}

/** Район Баку с приблизительным центром (map-taxonomy.json → districts.lat/lng). */
type DistrictCentre = TaxItem & { lat: number; lng: number };

/** Станция ближе этого (км) — район берётся из районов станции. */
export const DISTRICT_BY_METRO_KM = 1.2;

/**
 * Район Баку по пину — по координатам, не по Google (16.10.2026: подтверждение
 * «Rayon: X, metro: Y — düzdür?»). У станции метро (ближе DISTRICT_BY_METRO_KM)
 * — один из её районов, ближайший центром; дальше от метро — ближайший центр
 * района. Приблизительно: человек видит район и может его поправить.
 */
export function districtByPin(lat: number, lng: number): string {
  const k = Math.cos((lat * Math.PI) / 180);
  const centres = DISTRICTS as DistrictCentre[];
  const m = nearestMetro(lat, lng);
  const pool = metroDistanceKm(m, lat, lng) <= DISTRICT_BY_METRO_KM ? centres.filter((d) => m.districts.includes(d.code)) : centres;
  let best = (pool.length ? pool : centres)[0];
  let bestD = Infinity;
  for (const d of pool.length ? pool : centres) {
    const dist = (d.lat - lat) ** 2 + ((d.lng - lng) * k) ** 2;
    if (dist < bestD) {
      bestD = dist;
      best = d;
    }
  }
  return best.code;
}

/** Станция ближе этого (км) — «у метро»; дальше предупреждение о районе не показываем. */
export const METRO_NEAR_KM = 2;

/** Расстояние до станции в км — та же плоская формула, для малых расстояний её хватает. */
export function metroDistanceKm(m: Metro, lat: number, lng: number): number {
  const k = Math.cos((lat * Math.PI) / 180);
  return Math.sqrt((m.lat - lat) ** 2 + ((m.lng - lng) * k) ** 2) * 111.32;
}

/**
 * Район анкеты не сходится с районом ближайшего метро (пин у Həzi Aslanov, а
 * район — Nərimanov). Только подсказка в форме, отправку не блокирует.
 * Станция дальше METRO_NEAR_KM — молчим: на окраине «ближайшее метро» ничего
 * не говорит о районе.
 */
export function districtMetroMismatch(district: string, lat: number, lng: number): Metro | null {
  if (!district) return null;
  const m = nearestMetro(lat, lng);
  if (metroDistanceKm(m, lat, lng) > METRO_NEAR_KM) return null;
  return m.districts.includes(district) ? null : m;
}

/**
 * Грубая рамка страны (с Нахчываном): пин за её пределами анкета не примет.
 * Отсекает только дальние точки (Ереван, Тегеран, Москва) — приграничные
 * города соседей (Тбилиси, Горис) внутри рамки: им cityByPin даст ближайший
 * райцентр (src/lib/map-cities.ts) — проверит модерация.
 */
export function inAzerbaijan(lat: number, lng: number): boolean {
  return lat >= 38.3 && lat <= 41.95 && lng >= 44.7 && lng <= 50.9;
}
